'use client'

import { useState, useRef, useEffect, useCallback } from 'react'

import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  ButtonAsA,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import Image from '@/components/Image'
import ComponentErrorBoundary from '@/helpers/ComponentErrorBoundary'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import type { HeroBannerDataProps } from './useHeroBannerData'

const HeroBannerCarouselContainer = styled.section`
  position: relative;
  overflow: hidden;
  background: ${Colors.Semantic.BStock.default.lightBackground};
`

const HeroBannerCarouselTrack = styled.div<{
  $isTransitionEnabled: boolean
  $currentSlide: number
}>`
  display: flex;
  transition: ${({ $isTransitionEnabled }) =>
    $isTransitionEnabled ? 'transform 0.5s ease' : 'none'};
  transform: translateX(-${({ $currentSlide }) => $currentSlide * 100}%);
`

/**
 * `$background` is set only for a CMS banner an editor chose to render without artwork. With an
 * image the image covers the slide, so the colour would never be seen.
 */
const HeroBannerCarouselSlide = styled.div<{ $background?: string }>`
  display: flex;
  align-items: center;
  position: relative;
  flex: 0 0 100%;
  padding: 0 3rem;
  ${({ $background }) =>
    $background ? `background-color: ${$background};` : ''}

  @media ${Breakpoints.min.medium} {
    padding: 0 9rem;
  }
`

const HeroBannerCarouselSlideImage = styled(Image)`
  position: absolute;
  width: 100%;
  height: 100%;
  inset: 0;
  object-fit: cover;
  z-index: 1;
`

const HeroBannerCarouselSlideContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  position: relative;
  text-align: center;
  z-index: 3;
  width: 100%;
  max-width: var(--maxPageWidth);
  margin: 0 auto;
  padding: 1.5rem 0;
  /* White reads over photography; a banner on a pale background overrides it. */
  color: ${Colors.Semantic.Neutral.white};

  :nth-child(1) {
    max-width: 60rem;
  }

  :nth-child(2) {
    max-width: 55rem;
  }

  @media ${Breakpoints.min.medium} {
    padding: 3rem 0;
    text-align: left;
    align-items: flex-start;
  }
`

const HeroBannerCarouselSlideTitle = styled.h2`
  ${Typography.Title1}
`

const HeroBannerCarouselFirstSlideTitle = styled.h1`
  ${Typography.Title1}
`

const HeroBannerCarouselSlideSubtitle = styled.p`
  ${Typography.Body1_Semibold}
`

const HeroBannerCarouselNavBtn = styled.button<{
  $side: 'left' | 'right'
}>`
  position: absolute;
  ${({ $side }) => $side}: 0;
  top: 50%;
  z-index: 24;
  transform: translateY(-50%);
  margin: 0 0.5rem;
  color: ${Colors.Semantic.Neutral.white};
  opacity: 0.5;
  transition: 0.3s ease-out;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }

  @media ${Breakpoints.min.medium} {
    margin: 0 3rem;

    svg {
      width: 3rem;
      height: 3rem;
    }
  }
`

/**
 * Hero Banner Carousel component. Takes in an array of hero banner objects
 * containing a title, subtitle, banner image, and optional cta links. Banners
 * automatically rotate every 8 seconds by default with an option to increase
 * the interval time. Banners seamlessly rotate in an 'infinite' loop.
 */
const HeroBannerCarousel = ({
  banners,
  rotateInterval = 8000,
}: HeroBannerDataProps) => {
  const intl = useIntl()

  /**
   * Whether there is anything to loop between. `useHeroBannerData` wraps the real banners in a
   * leading and trailing clone, so a looping carousel always arrives with at least three slides
   * and starts on index 1, the first real one. A lone banner arrives unwrapped — there is nothing
   * to clone it against — so it starts on index 0 and neither rotates nor offers arrows.
   */
  const isLooping = banners.length > 1

  const [currentSlide, setCurrentSlide] = useState(isLooping ? 1 : 0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const heroBannerTrackRef = useRef<HTMLDivElement>(null)

  const nextSlide = useCallback(() => {
    if (!isTransitioning) {
      setIsTransitioning(true)
      setCurrentSlide((prevSlide) => prevSlide + 1)
    }
  }, [isTransitioning])

  const prevSlide = useCallback(() => {
    if (!isTransitioning) {
      setIsTransitioning(true)
      setCurrentSlide((prevSlide) => prevSlide - 1)
    }
  }, [isTransitioning])

  const onTransitionEnd = () => {
    setIsTransitioning(false)

    // replace cloned first banner with natural first banner in array without a transition effect
    if (currentSlide === banners.length - 1) {
      setCurrentSlide(1)
    }

    // replace cloned last banner with natural last banner in array without a transition effect
    if (currentSlide === 0) {
      setCurrentSlide(banners.length - 2)
    }
  }

  const { trackButtonClicked } = useAnalytics()

  const handlePrimaryButtonClick = useCallback(
    (href: string, buttonText: string) => {
      // Segment analytics event and metadata transcribed from old wordpress code
      // TODO: Update entity_type to based on the role of the user once we
      // implement user authentication.
      trackButtonClicked({
        screenName: 'homepage',
        buttonName: 'banner_cta',
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        cta_name: buttonText.toLowerCase().replace(/\s+/g, '_'),
        url: document.URL,
        referrer: document.referrer,
        target_url: href,
      })
    },
    [trackButtonClicked]
  )

  useEffect(() => {
    if (!isLooping) return

    const autoScroll = setInterval(() => {
      nextSlide()
    }, rotateInterval)

    return () => clearInterval(autoScroll)
  }, [isLooping, nextSlide, rotateInterval])

  return (
    <HeroBannerCarouselContainer>
      {isLooping && (
        <HeroBannerCarouselNavBtn
          $side="left"
          aria-label={intl.formatMessage({
            id: 'Common.slide.previous',
          })}
          onClick={prevSlide}
          disabled={isTransitioning}
        >
          <FontAwesomeIcon icon={faAngleLeft} />
        </HeroBannerCarouselNavBtn>
      )}
      <HeroBannerCarouselTrack
        ref={heroBannerTrackRef}
        $currentSlide={currentSlide}
        $isTransitionEnabled={isTransitioning}
        onTransitionEnd={onTransitionEnd}
        data-testid="hero-banner-carousel-track"
      >
        {banners.map((banner, idx) => (
          <HeroBannerCarouselSlide
            /*
              The index is the position in the loop, which is what a slide is. It is stable for as
              long as the component lives — CMS banners arrive resolved and ordered as a prop from
              a server render, and nothing reorders them here. A banner id would also be wrong: the
              leading and trailing clones repeat one, so two slides would share a key.
            */
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            $background={banner.backgroundColor}
          >
            <ComponentErrorBoundary>
              {/* Absent for a banner rendered on a plain background instead of artwork. */}
              {banner.image ? (
                <HeroBannerCarouselSlideImage
                  src={banner.image}
                  aria-hidden
                  alt={banner.title}
                  loading="eager"
                  width={1920}
                  height={400}
                  priority
                />
              ) : null}

              <HeroBannerCarouselSlideContent
                style={
                  banner.textColor ? { color: banner.textColor } : undefined
                }
              >
                {/*
                  The first real slide carries the page's h1. It sits at index 1 in a looping
                  carousel, behind the leading clone — but a lone banner is unwrapped, so there it
                  is index 0. Keyed off the index alone, a single-banner homepage had no h1 at all.
                */}
                {idx === (isLooping ? 1 : 0) ? (
                  <HeroBannerCarouselFirstSlideTitle>
                    {banner.title}
                  </HeroBannerCarouselFirstSlideTitle>
                ) : (
                  <HeroBannerCarouselSlideTitle>
                    {banner.title}
                  </HeroBannerCarouselSlideTitle>
                )}

                {/*
                  A text node, not `dangerouslySetInnerHTML`. This was markup-capable while every
                  subtitle was committed in this repo, and none of them ever used it — no tags and
                  no entities in the bundled set. A CMS editor now supplies the value, so the same
                  call would run whatever they typed in a visitor's browser.
                */}
                <HeroBannerCarouselSlideSubtitle>
                  {banner.subtitle}
                </HeroBannerCarouselSlideSubtitle>

                <ButtonAsA
                  appearance="primary"
                  href={banner.primaryBtnLink}
                  target="_blank"
                  type="button"
                  onClick={() =>
                    handlePrimaryButtonClick(
                      banner.primaryBtnLink || '',
                      banner.primaryBtnText || ''
                    )
                  }
                >
                  {banner.primaryBtnText}
                </ButtonAsA>
              </HeroBannerCarouselSlideContent>
            </ComponentErrorBoundary>
          </HeroBannerCarouselSlide>
        ))}
      </HeroBannerCarouselTrack>
      {isLooping && (
        <HeroBannerCarouselNavBtn
          $side="right"
          aria-label={intl.formatMessage({
            id: 'Common.slide.next',
          })}
          onClick={nextSlide}
          disabled={isTransitioning}
        >
          <FontAwesomeIcon icon={faAngleRight} />
        </HeroBannerCarouselNavBtn>
      )}
    </HeroBannerCarouselContainer>
  )
}

export default HeroBannerCarousel
